// src/Layout.js
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { auth } from "./firebase";
import Navbar from './Navbar';
import { signOut, onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { motion, AnimatePresence } from "framer-motion";
import ChatRequestModal from "./ChatRequestModal";
import "./Footer.css";

const APP_ASSETS = {
  logo: "/Nabi UI Assets/Assets_Images/Logo placeholder.png",
  instagram: "/Nabi UI Assets/Assets_Icon/Icon_Instagram.svg",
  linkedin: "/Nabi UI Assets/Assets_Icon/Icon_Linkedin.svg",
};

export default function Layout() {
  const [user, setUser] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [globalCart, setGlobalCart] = useState([]);  // ✅ ADD THIS
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [globalPincode, setGlobalPincode] = useState("");
  const [isPincodeModalOpen, setIsPincodeModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // First set the basic firebase user so UI doesn't look logged out
        setUser(firebaseUser);

        try {
          // Then fetch full userData from Firestore
          const q = query(collection(db, "users"), where("uid", "==", firebaseUser.uid));
          const snap = await getDocs(q);
          
          if (!snap.empty) {
            const userData = snap.docs[0].data();
            sessionStorage.setItem("userData", JSON.stringify(userData));
            setUser({ ...firebaseUser, ...userData });
          }
        } catch (err) {
          console.error("Error syncing user data in Layout:", err);
        }
      } else {
        setUser(null);
        sessionStorage.removeItem("userData");
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setIsMenuOpen(false);
    navigate("/");
  };

  // --- CART HELPERS ---
  const addToCart = (product, qty = 1) => {
    if (!user) {
      setIsLoginOpen(true);
      alert("Please login to add items to your cart.");
      return;
    }
    const existing = globalCart.find(item => item.id === product.id);
    if (existing) {
      setGlobalCart(globalCart.map(item => item.id === product.id ? { ...item, qty: item.qty + qty } : item));
    } else {
      setGlobalCart([...globalCart, { ...product, qty }]);
    }
    setIsCartOpen(true);
  };

  const removeFromCart = (id) => {
    setGlobalCart(globalCart.filter(item => item.id !== id));
  };

  const updateQty = (id, delta) => {
    setGlobalCart(globalCart.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.qty + delta);
        return { ...item, qty: newQty };
      }
      return item;
    }));
  };

  const cartTotal = globalCart.reduce((acc, item) => acc + (item.price * item.qty), 0);

  const hideNavbar = location.pathname === "/verify" || location.pathname === "/therapistdashboard";

  return (
    <div style={{ backgroundColor: "#FFF7F1", minHeight: "100vh" }}>

      {!hideNavbar && (
        <Navbar
          user={user}
          globalCart={globalCart}
          setIsCartOpen={setIsCartOpen}
          isLoginOpen={isLoginOpen}
          setIsLoginOpen={setIsLoginOpen}
        />
      )}

      {/* --- GLOBAL CART SIDEBAR --- */}
      <AnimatePresence>
        {isCartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={styles.overlay}
              onClick={() => setIsCartOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              style={styles.cartSidebar}
            >
              <div style={styles.cartHeader}>
                <h3 style={{ fontWeight: "normal" }}>Cart ({globalCart.length})</h3>
                <button onClick={() => setIsCartOpen(false)} style={styles.closeBtn}>✕</button>
              </div>

              <div style={styles.cartItemsScroll}>
                {globalCart.length === 0 ? <p style={{ textAlign: 'center', marginTop: '20px', color: '#777' }}>Your cart is empty</p> : null}
                {globalCart.map(item => (
                  <div key={item.id} style={styles.cartItemRow}>
                    <img src={item.image} alt="" style={styles.cartItemImg} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <h4 style={styles.cartItemName}>{item.name}</h4>
                        <button onClick={() => removeFromCart(item.id)} style={styles.trashBtn}>🗑</button>
                      </div>
                      <p style={{ fontSize: '12px', color: '#777' }}>₹{item.price.toFixed(2)}</p>
                      <div style={styles.qtyControlSm}>
                        <button onClick={() => updateQty(item.id, -1)}>-</button>
                        <span>{item.qty}</span>
                        <button onClick={() => updateQty(item.id, 1)}>+</button>
                      </div>
                    </div>
                    <span style={{ alignSelf: 'flex-end', fontWeight: 'normal' }}>₹{(item.price * item.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div style={styles.cartFooter}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', fontWeight: 'normal' }}>
                  <span>Total</span>
                  <span>₹{cartTotal.toFixed(2)}</span>
                </div>
                <button
                  onClick={() => {
                    if (!user) {
                      setIsCartOpen(false);
                      setIsLoginOpen(true);
                      alert("Please login to proceed to checkout.");
                    } else if (!globalPincode || globalPincode.length !== 6) {
                      setIsPincodeModalOpen(true);
                    } else {
                      setIsCartOpen(false);
                      navigate("/store/checkout");
                    }
                  }}
                  style={styles.checkoutBtn}
                  disabled={globalCart.length === 0}
                >
                  Checkout
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* PAGE CONTENT - This is where your pages will render */}
      <div style={!hideNavbar ? { marginTop: '70px' } : {}}>
        <Outlet context={{ user, globalCart, setGlobalCart, addToCart, removeFromCart, updateQty, setIsCartOpen, setIsLoginOpen, globalPincode, setGlobalPincode }} />
      </div>

      {/* FOOTER - Only show if not on verify page */}
      {!hideNavbar && (
        <footer className="footer">
          <div className="footer-top">
            {/* Brand Column */}
            <div className="footer-brand">
              <img src={APP_ASSETS.logo} alt="Nabi" className="footer-logo" />
              <div>
                <h2 className="footer-brand-name">World of Nabi</h2>
                <p className="footer-tagline">Just Be!</p>
              </div>
            </div>

            {/* Links Columns */}
            <div className="footer-links-container">
              <div className="footer-column">
                <h4 className="footer-col-head">Explore</h4>
                <span onClick={() => navigate("/")} className="footer-link">Home</span>
                <span onClick={() => navigate("/nabi-health")} className="footer-link">Nabi Health</span>
                <span onClick={() => navigate("/store")} className="footer-link">Nabi Store</span>
                <span onClick={() => navigate("/blog")} className="footer-link">Nabi Stories</span>
                <span onClick={() => navigate("/events")} className="footer-link">Nabi Events</span>
              </div>

              <div className="footer-column">
                <h4 className="footer-col-head">Careers</h4>
                <span onClick={() => navigate("/careers")} className="footer-link">Nabi Careers</span>
              </div>

              <div className="footer-column">
                <h4 className="footer-col-head">Reach Out</h4>
                <span onClick={() => window.open("https://instagram.com/nabicommunity", "_blank")} className="footer-link">@nabicommunity</span>
                <span onClick={() => window.location.href = "mailto:reachout@nabi.health"} className="footer-link">reachout@nabi.health</span>
              </div>

              <div className="footer-column">
                <h4 className="footer-col-head">Information</h4>
                <span onClick={() => navigate("/terms")} className="footer-link">Terms of Service</span>
                <span onClick={() => navigate("/privacy")} className="footer-link">Privacy Policy</span>
                <span onClick={() => navigate("/shipping-return")} className="footer-link">Shipping and Return Policy</span>
              </div>
            </div>
          </div>

          <div className="footer-divider"></div>

          <div className="footer-bottom">
            <p className="footer-copyright">Copyright@ nabi 2025. All rights reserved.</p>
            <div className="footer-social">
              <img src={APP_ASSETS.instagram} alt="Instagram" className="footer-social-icon" onClick={() => window.open("https://www.instagram.com/nabicommunity/", "_blank")} />
              <img src={APP_ASSETS.linkedin} alt="LinkedIn" className="footer-social-icon" onClick={() => window.open("https://www.linkedin.com/company/nabi-health/", "_blank")} />
            </div>
          </div>
        </footer>
      )}

      <AnimatePresence>
        {isPincodeModalOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.pincodeModal}>
              <h3 style={{marginTop: 0, fontWeight: 'normal'}}>Enter Delivery Pincode</h3>
              <p style={{fontSize: '14px', color: '#555', marginBottom: '20px'}}>Please enter your delivery pincode to calculate shipping charges.</p>
              <input 
                 type="text" 
                 maxLength={6} 
                 value={globalPincode}
                 onChange={(e) => setGlobalPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                 style={styles.modalInput}
                 placeholder="6-digit Pincode"
              />
              <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                 <button onClick={() => setIsPincodeModalOpen(false)} style={styles.modalCancelBtn}>Cancel</button>
                 <button 
                   onClick={() => {
                     if(globalPincode.length === 6) {
                       setIsPincodeModalOpen(false);
                       setIsCartOpen(false);
                       navigate("/store/checkout");
                     } else {
                       alert("Please enter a valid 6-digit pincode.");
                     }
                   }}
                   style={styles.modalConfirmBtn}
                 >
                   Proceed
                 </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 15000 },
  cartSidebar: { fontWeight: "normal", position: 'fixed', top: 0, right: 0, width: '400px', maxWidth: '100vw', height: '100vh', background: 'white', zIndex: 15001, padding: '30px', display: 'flex', flexDirection: 'column', boxShadow: '-5px 0 20px rgba(0,0,0,0.1)', fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' },
  cartHeader: { fontWeight: "normal", display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '20px', marginBottom: '20px' },
  cartItemsScroll: { flex: 1, overflowY: 'auto', marginBottom: '20px' },
  cartItemRow: { display: 'flex', gap: '15px', marginBottom: '20px', borderBottom: '1px solid #f9f9f9', paddingBottom: '15px' },
  cartItemImg: { width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px' },
  cartItemName: { margin: '0 0 5px', fontSize: '14px', fontWeight: 'normal' },
  trashBtn: { border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px' },
  qtyControlSm: { display: 'flex', border: '1px solid #ddd', borderRadius: '4px', width: '80px', justifyContent: 'space-between', padding: '2px 8px', marginTop: '5px', fontSize: '12px' },
  cartFooter: { borderTop: '1px solid #eee', paddingTop: '20px', background: 'white' },
  checkoutBtn: { width: '100%', padding: '12px', backgroundColor: '#F07167', color: 'white', border: 'none', borderRadius: '5px', fontWeight: 'normal', cursor: 'pointer', marginBottom: '10px' },
  closeBtn: { border: "none", background: "none", cursor: "pointer", fontSize: "18px" },
  floatingChatBtn: {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    backgroundColor: '#003049',
    color: 'white',
    border: 'none',
    borderRadius: '50px',
    padding: '12px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    boxShadow: '0 10px 25px rgba(0,48,73,0.3)',
    cursor: 'pointer',
    zIndex: 2000,
    transition: 'all 0.3s ease',
    fontFamily: "'Syne', sans-serif"
  },
  chatBtnLabel: {
    fontWeight: '600',
    fontSize: '14px'
  },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pincodeModal: { background: 'white', padding: '30px', borderRadius: '15px', width: '90%', maxWidth: '400px', fontFamily: "'Syne', sans-serif" },
  modalInput: { width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', outline: 'none', fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' },
  modalCancelBtn: { flex: 1, padding: '12px', border: '1px solid #ddd', background: 'white', borderRadius: '8px', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  modalConfirmBtn: { flex: 1, padding: '12px', border: 'none', background: '#F07167', color: 'white', borderRadius: '8px', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }
};